import React, { useState, useEffect } from 'react';
import { companyService } from '../services/companyService';
import { InvoiceNumberingTemplate, InvoiceNumberingSettings, InvoiceNumberingSettingsRequest } from '../types/company';
import { useTranslation } from '../hooks/useTranslation';

interface CorrectionInvoiceNumberingSettingsProps {
  companyId: string;
}

const CorrectionInvoiceNumberingSettingsComponent: React.FC<CorrectionInvoiceNumberingSettingsProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<InvoiceNumberingSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewNumber, setPreviewNumber] = useState<string>('');
  const [previewDate, setPreviewDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [error, setError] = useState<string>('');

  // Загружаем настройки при монтировании компонента
  useEffect(() => {
    loadSettings();
  }, [companyId]);

  // Обновляем предварительный просмотр при изменении настроек
  useEffect(() => {
    if (settings && companyId) {
      updatePreview();
    }
  }, [settings, previewDate, companyId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await companyService.getCorrectionInvoiceNumberingSettings(companyId);
      setSettings(data);
      setError('');
    } catch (err) {
      setError(t('invoiceNumbering.loadError'));
      console.error('Error loading correction invoice numbering settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const updatePreview = async () => {
    if (!settings || !companyId) {
      setPreviewNumber('');
      return;
    }
    
    try {
      setPreviewNumber(''); // Очищаем перед загрузкой
      const data = await companyService.previewCorrectionInvoiceNumber(companyId, previewDate);
      console.log('Preview correction number response:', data);
      if (data && data.correctionNumber) {
        setPreviewNumber(data.correctionNumber);
      } else {
        console.warn('No correctionNumber in response:', data);
        setPreviewNumber(t('common.error') || 'Error');
      }
    } catch (err) {
      console.error('Error previewing correction invoice number:', err);
      setPreviewNumber(t('common.error') || 'Error');
    }
  };

  const handleTemplateChange = (template: InvoiceNumberingTemplate) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      template
    });
  };

  const handleSuffixChange = (suffix: string) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      suffix
    });
  };

  const handleSequenceChange = (sequence: number) => {
    if (!settings) return;
    
    setSettings({
      ...settings,
      currentSequence: sequence
    });
  };

  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      const request: InvoiceNumberingSettingsRequest = {
        template: settings.template,
        suffix: settings.suffix,
        currentSequence: settings.currentSequence
      };
      
      const updatedSettings = await companyService.updateCorrectionInvoiceNumberingSettings(companyId, request);
      setSettings(updatedSettings);
      setError('');
    } catch (err) {
      setError(t('invoiceNumbering.saveError'));
      console.error('Error saving correction invoice numbering settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const getTemplateDescription = (template: InvoiceNumberingTemplate): string => {
    return t(`correctionInvoiceNumbering.templateDescription.${template}`) || t(`invoiceNumbering.templateDescription.${template}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{t('invoiceNumbering.loadError')}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-900 mb-6">{t('correctionInvoiceNumbering.title')}</h2>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Шаблон нумерации */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('invoiceNumbering.template')}
          </label>
          <div className="space-y-2">
            {Object.values(InvoiceNumberingTemplate).map((template) => (
              <label key={template} className="flex items-center">
                <input
                  type="radio"
                  name="correctionTemplate"
                  value={template}
                  checked={settings.template === template}
                  onChange={() => handleTemplateChange(template)}
                  className="mr-3"
                />
                <div>
                  <div className="font-medium">{template}</div>
                  <div className="text-sm text-gray-500">{getTemplateDescription(template)}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Суффикс (только для NN_MM_YYYY_ID) */}
        {settings.template === InvoiceNumberingTemplate.NN_MM_YYYY_ID && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('invoiceNumbering.suffix')}
            </label>
            <input
              type="text"
              value={settings.suffix || ''}
              onChange={(e) => handleSuffixChange(e.target.value)}
              placeholder={t('correctionInvoiceNumbering.suffixPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {/* Текущий порядковый номер */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('invoiceNumbering.currentSequence')}
          </label>
          <input
            type="number"
            min="0"
            value={settings.currentSequence}
            onChange={(e) => handleSequenceChange(parseInt(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-sm text-gray-500 mt-1">
            {t('correctionInvoiceNumbering.nextNumber').replace('{number}', (settings.currentSequence + 1).toString())}
          </p>
        </div>

        {/* Предварительный просмотр */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('invoiceNumbering.preview')}
          </label>
          <div className="flex gap-2">
            <input
              type="date"
              value={previewDate}
              onChange={(e) => setPreviewDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg">
              {previewNumber || t('common.loading')}
            </div>
          </div>
        </div>

        {/* Кнопка сохранения */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? t('invoiceNumbering.saving') : t('invoiceNumbering.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CorrectionInvoiceNumberingSettingsComponent;

